import React, { useState, useEffect, useRef } from 'react';
import {
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Box,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Stack,
  Divider,
  Chip,
  useMediaQuery,
  useTheme
} from '@mui/material';
import { KeyboardArrowLeft } from '@mui/icons-material';
import axios from '../utils/axios';
import { translations } from './paymentLookupTranslations';

const formatCurrency = (n, locale = 'en-US') =>
  (Number(n) || 0).toLocaleString(locale, { style: 'currency', currency: 'USD' });

const formatDate = (d, locale = 'en-US') => {
  if (!d) return '';
  try {
    return new Date(d).toLocaleDateString(locale, { timeZone: 'America/New_York' });
  } catch {
    return '';
  }
};

const PaymentLookup = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Language toggle — shares the same localStorage key as the public RE registration
  // form so switching language on one page persists to the other.
  const [language, setLanguage] = useState(() => {
    try {
      const saved = window.localStorage.getItem('reRegistrationLanguage');
      return saved === 'es' ? 'es' : 'en';
    } catch {
      return 'en';
    }
  });
  const t = translations[language];
  const handleLanguageChange = (lang) => {
    setLanguage(lang);
    try { window.localStorage.setItem('reRegistrationLanguage', lang); } catch {}
  };
  const fc = (n) => formatCurrency(n, t.locale);
  const fd = (d) => formatDate(d, t.locale);

  // School year context (loaded from public settings endpoint)
  const [availableSchoolYears, setAvailableSchoolYears] = useState([]);
  const [selectedSchoolYear, setSelectedSchoolYear] = useState('');

  // Step state — 'lookup' shows email form; 'select' shows matching registrations; 'pay' shows Square form
  const [step, setStep] = useState('lookup');
  const [email, setEmail] = useState('');
  const [looking, setLooking] = useState(false);
  const [registrations, setRegistrations] = useState([]);
  const [selectedReg, setSelectedReg] = useState(null);
  const [paymentAmount, setPaymentAmount] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Square handles
  const [squareReady, setSquareReady] = useState(false);
  const [squareCard, setSquareCard] = useState(null);
  // One Square idempotency key per payment attempt, kept across retries after
  // an ambiguous failure so the same payment can never be charged twice.
  const paymentIdemKeyRef = useRef(null);

  // Load available school years on mount; default the dropdown to the current year.
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const res = await axios.get('/religious-education/settings/status-public');
        const current = res.data?.currentSchoolYear || '';
        const list = Array.isArray(res.data?.availableSchoolYears) ? res.data.availableSchoolYears : [];
        // Ensure the current year is always present in the dropdown even if it isn't yet
        // recorded in availableSchoolYears.
        const merged = current && !list.includes(current) ? [current, ...list] : list;
        setAvailableSchoolYears(merged);
        setSelectedSchoolYear(current);
      } catch (e) {
        // Non-fatal — the user can still proceed with an empty year filter.
        console.error('Failed to load school year settings:', e);
      }
    };
    fetchSettings();
  }, []);

  // Initialize the Square Web Payments SDK when the payment step is entered. The
  // SDK is loaded on demand so it doesn't slow the initial page render. The
  // applicationId/locationId come from the server-side /square-config endpoint
  // so dev and prod work without changing client code.
  useEffect(() => {
    if (step !== 'pay') return;
    if (squareReady) return;

    let cancelled = false;

    const init = async () => {
      try {
        const configRes = await axios.get('/square-config');
        const { applicationId, locationId } = configRes.data || {};
        if (!applicationId || !locationId) {
          throw new Error('Square configuration is incomplete.');
        }

        const isProd = window.location.hostname.includes('stbridgetup.org');
        const sdkUrl = isProd
          ? 'https://web.squarecdn.com/v1/square.js'
          : 'https://sandbox.web.squarecdn.com/v1/square.js';

        const ensureSdk = () =>
          new Promise((resolve, reject) => {
            if (window.Square) return resolve();
            const existing = document.querySelector(`script[src="${sdkUrl}"]`);
            if (existing) {
              existing.addEventListener('load', () => resolve());
              existing.addEventListener('error', reject);
              return;
            }
            const script = document.createElement('script');
            script.src = sdkUrl;
            script.onload = () => resolve();
            script.onerror = reject;
            document.body.appendChild(script);
          });

        await ensureSdk();
        if (cancelled) return;

        const payments = window.Square.payments(applicationId, locationId);
        const card = await payments.card();
        await card.attach('#payment-card-container');
        if (cancelled) {
          try { await card.destroy(); } catch (_) { /* noop */ }
          return;
        }
        setSquareCard(card);
        setSquareReady(true);
      } catch (e) {
        console.error('Square init failed:', e);
        setError(t.paymentSystemFailed);
      }
    };

    init();
    return () => {
      cancelled = true;
    };
  }, [step, squareReady]);

  const handleLookup = async () => {
    setError(null);
    setSuccess(null);
    const trimmedEmail = email.trim();
    if (!trimmedEmail) {
      setError(t.pleaseEnterEmail);
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail)) {
      setError(t.pleaseEnterValidEmail);
      return;
    }

    setLooking(true);
    try {
      const params = selectedSchoolYear ? { schoolYear: selectedSchoolYear } : {};
      const res = await axios.get(`/religious-education/lookup/${encodeURIComponent(trimmedEmail)}`, { params });
      const results = Array.isArray(res.data) ? res.data : [];
      if (results.length === 0) {
        setError(t.noRegistrationsFound(trimmedEmail, selectedSchoolYear));
        setRegistrations([]);
        return;
      }
      setRegistrations(results);
      // Auto-select the single match so the user lands on the payment summary immediately.
      if (results.length === 1) {
        setSelectedReg(results[0]);
      }
      setStep('select');
    } catch (e) {
      if (e?.response?.status === 404) {
        setError(t.noRegistrationsFound(trimmedEmail, selectedSchoolYear));
      } else {
        setError(e?.response?.data?.message || t.lookupFailed);
      }
      setRegistrations([]);
    } finally {
      setLooking(false);
    }
  };

  const handleProceedToPayment = () => {
    if (!selectedReg) {
      setError(t.pleaseSelectRegistration);
      return;
    }
    if (selectedReg.remainingBalance <= 0) {
      setError(t.noRemainingBalance);
      return;
    }
    setError(null);
    // Default the amount to the full remaining balance for convenience.
    setPaymentAmount(selectedReg.remainingBalance.toFixed(2));
    setStep('pay');
  };

  const handleBackToLookup = () => {
    setRegistrations([]);
    setSelectedReg(null);
    setError(null);
    setSuccess(null);
    setStep('lookup');
  };

  const handleBackToSelect = () => {
    if (squareCard) {
      try { squareCard.destroy(); } catch (_) { /* noop */ }
    }
    setSquareCard(null);
    setSquareReady(false);
    setError(null);
    setSuccess(null);
    setStep('select');
  };

  const handleProcessPayment = async () => {
    setError(null);
    setSuccess(null);
    const amount = parseFloat(paymentAmount);
    if (!amount || amount <= 0) {
      setError(t.pleaseEnterValidAmount);
      return;
    }
    if (amount > selectedReg.remainingBalance + 0.001) {
      setError(t.paymentExceedsBalance(fc(selectedReg.remainingBalance)));
      return;
    }
    if (!squareCard) {
      setError(t.paymentFormNotReady);
      return;
    }

    setSubmitting(true);
    try {
      const tokenResult = await squareCard.tokenize();
      if (tokenResult.status !== 'OK') {
        const msg = (tokenResult.errors || []).map(e => e.message).join(', ') || t.cardDetailsInvalid;
        throw new Error(msg);
      }

      if (!paymentIdemKeyRef.current) {
        paymentIdemKeyRef.current = window.crypto?.randomUUID
          ? window.crypto.randomUUID()
          : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
      }
      const res = await axios.post(`/religious-education/payment/${selectedReg.id}`, {
        paymentAmount: amount,
        squarePaymentToken: tokenResult.token,
        paymentIdempotencyKey: paymentIdemKeyRef.current
      });
      // Payment settled — the next payment is a new attempt with a new key.
      paymentIdemKeyRef.current = null;

      if (res.data?.pendingManualReview) {
        // Charge succeeded; the office will finish the record manually.
        setSuccess(res.data.message || t.paymentSuccess(fc(amount), ''));
        if (squareCard) {
          try { squareCard.destroy(); } catch (_) { /* noop */ }
        }
        setSquareCard(null);
        setSquareReady(false);
        setStep('select');
        return;
      }

      const newBalance = Number(res.data?.newBalance) || 0;
      setSuccess(t.paymentSuccess(fc(amount), fc(newBalance)));

      // Refresh the registration in-place so the receipt/history reflects the new state
      // and the user can make another partial payment if they want without re-doing the lookup.
      setSelectedReg(prev => prev ? {
        ...prev,
        totalPaid: (prev.totalPaid || 0) + amount,
        remainingBalance: newBalance,
        paymentStatus: res.data?.paymentStatus || prev.paymentStatus,
        transactions: [
          ...(prev.transactions || []),
          { amount, date: new Date().toISOString(), method: 'online', reference: res.data?.paymentId }
        ]
      } : prev);
      setRegistrations(prev => prev.map(r => r.id === selectedReg.id ? {
        ...r,
        totalPaid: (r.totalPaid || 0) + amount,
        remainingBalance: newBalance,
        paymentStatus: res.data?.paymentStatus || r.paymentStatus,
        transactions: [
          ...(r.transactions || []),
          { amount, date: new Date().toISOString(), method: 'online', reference: res.data?.paymentId }
        ]
      } : r));
      setPaymentAmount(newBalance > 0 ? newBalance.toFixed(2) : '');

      // If the balance is now zero, drop back to the selection view so the family
      // can see the "Paid in Full" state. Otherwise stay on the payment screen.
      if (newBalance <= 0) {
        if (squareCard) {
          try { squareCard.destroy(); } catch (_) { /* noop */ }
        }
        setSquareCard(null);
        setSquareReady(false);
        setStep('select');
      }
    } catch (e) {
      // 400/402 mean no charge happened — safe to use a fresh key on retry.
      // Anything else is ambiguous: keep the key so a retry can't double-charge.
      const status = e?.response?.status;
      if (status === 400 || status === 402) {
        paymentIdemKeyRef.current = null;
      }
      setError(e?.response?.data?.message || e?.message || t.paymentFailed);
    } finally {
      setSubmitting(false);
    }
  };

  // ─── Renders ────────────────────────────────────────────────────────────────

  const renderLookupStep = () => (
    <Box>
      <Typography variant="h5" gutterBottom>
        {t.pageTitle}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {t.pageSubtitle}
      </Typography>

      <Stack spacing={2}>
        <FormControl fullWidth>
          <InputLabel id="payment-school-year-label">{t.schoolYear}</InputLabel>
          <Select
            labelId="payment-school-year-label"
            label={t.schoolYear}
            value={selectedSchoolYear}
            onChange={(e) => setSelectedSchoolYear(e.target.value)}
          >
            {availableSchoolYears.length === 0 && (
              <MenuItem value="" disabled>{t.noSchoolYears}</MenuItem>
            )}
            {availableSchoolYears.map(year => (
              <MenuItem key={year} value={year}>{year}</MenuItem>
            ))}
          </Select>
        </FormControl>

        <TextField
          label={t.emailAddress}
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !looking) handleLookup();
          }}
          fullWidth
          autoComplete="email"
        />

        <Button
          variant="contained"
          onClick={handleLookup}
          disabled={looking || !email.trim()}
          size={isMobile ? 'large' : 'medium'}
          fullWidth={isMobile}
          sx={{ minHeight: isMobile ? 48 : undefined }}
        >
          {looking ? <CircularProgress size={20} sx={{ color: 'white' }} /> : t.lookUpRegistration}
        </Button>
      </Stack>
    </Box>
  );

  const renderRegistrationCard = (reg) => {
    const selected = selectedReg && selectedReg.id === reg.id;
    const paid = reg.remainingBalance <= 0;
    return (
      <Paper
        key={reg.id}
        elevation={selected ? 3 : 1}
        onClick={() => setSelectedReg(reg)}
        sx={{
          p: 2,
          cursor: 'pointer',
          border: '2px solid',
          borderColor: selected ? 'primary.main' : 'divider',
          bgcolor: selected ? 'action.hover' : 'background.paper',
          transition: 'all 0.15s ease'
        }}
      >
        <Stack direction={isMobile ? 'column' : 'row'} justifyContent="space-between" spacing={1}>
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              {reg.familyName}{t.familySuffix}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t.schoolYearLabel(reg.schoolYear)}
            </Typography>
          </Box>
          <Box sx={{ textAlign: isMobile ? 'left' : 'right' }}>
            <Typography variant="body2">
              {t.totalLabel(fc(reg.totalAmount))}
            </Typography>
            <Typography variant="body2">
              {t.paidLabel(fc(reg.totalPaid))}
            </Typography>
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 700,
                color: paid ? 'success.main' : 'error.main'
              }}
            >
              {paid ? t.paidInFull : t.balanceLabel(fc(reg.remainingBalance))}
            </Typography>
          </Box>
        </Stack>
      </Paper>
    );
  };

  const renderSelectStep = () => (
    <Box>
      <Button
        onClick={handleBackToLookup}
        startIcon={<KeyboardArrowLeft />}
        sx={{ mb: 2 }}
      >
        {t.lookUpDifferentEmail}
      </Button>

      <Typography variant="h6" gutterBottom>
        {registrations.length === 1 ? t.yourRegistration : t.selectRegistration}
      </Typography>

      <Stack spacing={2} sx={{ mb: 2 }}>
        {registrations.map(renderRegistrationCard)}
      </Stack>

      {selectedReg && (
        <Paper variant="outlined" sx={{ p: 2, mt: 2 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
            {t.paymentHistory}
          </Typography>
          {selectedReg.transactions && selectedReg.transactions.length > 0 ? (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{t.date}</TableCell>
                    <TableCell>{t.amount}</TableCell>
                    <TableCell>{t.method}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {selectedReg.transactions
                    .slice()
                    .sort((a, b) => new Date(a.date) - new Date(b.date))
                    .map((tx, idx) => (
                      <TableRow key={`${tx.reference || idx}-${idx}`}>
                        <TableCell>{fd(tx.date)}</TableCell>
                        <TableCell>{fc(tx.amount)}</TableCell>
                        <TableCell sx={{ textTransform: 'capitalize' }}>{tx.method || '—'}</TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Typography variant="body2" color="text.secondary">
              {t.noPaymentsYet}
            </Typography>
          )}

          <Divider sx={{ my: 2 }} />

          <Button
            variant="contained"
            color="primary"
            disabled={selectedReg.remainingBalance <= 0}
            onClick={handleProceedToPayment}
            fullWidth={isMobile}
            size={isMobile ? 'large' : 'medium'}
            sx={{ minHeight: isMobile ? 48 : undefined }}
          >
            {selectedReg.remainingBalance <= 0
              ? t.fullyPaid
              : t.payBalanceButton(fc(selectedReg.remainingBalance))}
          </Button>
        </Paper>
      )}
    </Box>
  );

  const renderPayStep = () => (
    <Box>
      <Button
        onClick={handleBackToSelect}
        startIcon={<KeyboardArrowLeft />}
        sx={{ mb: 2 }}
      >
        {t.backToRegistration}
      </Button>

      <Typography variant="h6" gutterBottom>
        {t.makePayment}
      </Typography>

      <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: 'action.hover' }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          {selectedReg.familyName}{t.familySuffix}
        </Typography>
        <Typography variant="body2">{t.schoolYearLabel(selectedReg.schoolYear)}</Typography>
        <Typography variant="body2">{t.totalLabel(fc(selectedReg.totalAmount))}</Typography>
        <Typography variant="body2">{t.paidLabel(fc(selectedReg.totalPaid))}</Typography>
        <Typography variant="subtitle2" color="error" sx={{ fontWeight: 700 }}>
          {t.balanceDue(fc(selectedReg.remainingBalance))}
        </Typography>
      </Paper>

      <TextField
        label={t.paymentAmountLabel}
        type="number"
        value={paymentAmount}
        onChange={(e) => setPaymentAmount(e.target.value)}
        fullWidth
        inputProps={{ min: 0.01, max: selectedReg.remainingBalance, step: '0.01' }}
        sx={{ mb: 2 }}
      />

      <Box id="payment-card-container" sx={{ p: 1, border: '1px solid', borderColor: 'divider', borderRadius: 1, minHeight: 60, mb: 2 }} />

      {!squareReady && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <CircularProgress size={18} />
          <Typography variant="body2" color="text.secondary">{t.loadingPaymentForm}</Typography>
        </Box>
      )}

      <Button
        variant="contained"
        color="success"
        disabled={!squareReady || submitting}
        onClick={handleProcessPayment}
        fullWidth
        size={isMobile ? 'large' : 'medium'}
        sx={{ minHeight: isMobile ? 48 : undefined }}
      >
        {submitting ? <CircularProgress size={20} sx={{ color: 'white' }} /> : t.payButton(fc(parseFloat(paymentAmount || 0)))}
      </Button>
    </Box>
  );

  const LanguageToggle = () => (
    <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mb: 2 }}>
      <Chip
        label={t.languageLabel.en}
        size="small"
        color={language === 'en' ? 'primary' : 'default'}
        variant={language === 'en' ? 'filled' : 'outlined'}
        onClick={() => handleLanguageChange('en')}
      />
      <Chip
        label={t.languageLabel.es}
        size="small"
        color={language === 'es' ? 'primary' : 'default'}
        variant={language === 'es' ? 'filled' : 'outlined'}
        onClick={() => handleLanguageChange('es')}
      />
    </Stack>
  );

  return (
    <Container maxWidth="sm" sx={{ py: isMobile ? 2 : 4, px: isMobile ? 1 : 2 }}>
      <Paper sx={{ p: isMobile ? 2 : 4 }}>
        <LanguageToggle />
        {error && (
          <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" onClose={() => setSuccess(null)} sx={{ mb: 2 }}>
            {success}
          </Alert>
        )}

        {step === 'lookup' && renderLookupStep()}
        {step === 'select' && renderSelectStep()}
        {step === 'pay' && renderPayStep()}
      </Paper>
    </Container>
  );
};

export default PaymentLookup;
